import type { ContentActionMenuItem } from "@setplex/arcana"

import type { MediaTypes } from '@setplex/tria-api'

import { IconNames } from "/app/libraries/arcana/src/atoms/Icon/index.ts"

import { MediaTypes as MediaTypesEnum } from '@setplex/tria-api'
import { useUnit } from 'effector-react'
import { useCallback } from 'react'
import { model as i18nModel } from '~/core/i18n'
import { isContentWatchedEligible } from '~/features/content-watched/lib'
import { model as guestModel } from '~/features/subscriber/guest'
import { removeFromContinueWatching } from '../model'

type Params = {
  contentId?: number
  tvShowId?: number
  seasonId?: number | null
  isWithSeason?: boolean
  contentType?: MediaTypes | string | null
  name?: string
  analyticsContentType?: 'vod' | 'tvshow'
  analyticsContentName?: string
  analyticsProgramName?: string
  analyticsRouteName?: string
  locked?: boolean
  blockedByAcl?: boolean
}

export const useRemoveFromContinueWatchingAction = ({
  contentId,
  tvShowId,
  seasonId,
  isWithSeason,
  contentType,
  name,
  analyticsContentType,
  analyticsContentName,
  analyticsProgramName,
  analyticsRouteName,
  locked,
  blockedByAcl,
}: Params) => {
  const { removeFromCw, guest, i18n } = useUnit({
    removeFromCw: removeFromContinueWatching,
    guest: guestModel.$guest,
    i18n: i18nModel.$t,
  })
  const { t } = i18n

  const eligible =
    Boolean(contentId) &&
    isContentWatchedEligible({
      contentType,
      guest: Boolean(guest),
      blockedByAcl: Boolean(blockedByAcl),
      locked: Boolean(locked),
    })

  const handleRemoveSelect = useCallback(() => {
    if (!eligible || !contentId) return

    if (contentType === MediaTypesEnum.VOD) {
      removeFromCw({
        type: 'vod',
        vodId: contentId,
        name,
        analyticsContentType: analyticsContentType ?? 'vod',
        analyticsContentName: analyticsContentName ?? name,
        analyticsProgramName,
        analyticsRouteName,
      })
      return
    }

    if (contentType !== MediaTypesEnum.EPISODE || !tvShowId) return

    if (seasonId && isWithSeason !== false) {
      removeFromCw({
        type: 'episodeWithSeason',
        tvShowId,
        seasonId,
        episodeId: contentId,
        name,
        analyticsContentType: analyticsContentType ?? 'tvshow',
        analyticsContentName: analyticsContentName ?? name,
        analyticsProgramName,
        analyticsRouteName,
      })
      return
    }

    removeFromCw({
      type: 'episodeWithoutSeason',
      tvShowId,
      episodeId: contentId,
      name,
      analyticsContentType: analyticsContentType ?? 'tvshow',
      analyticsContentName: analyticsContentName ?? name,
      analyticsProgramName,
      analyticsRouteName,
    })
  }, [
    analyticsContentName,
    analyticsContentType,
    analyticsProgramName,
    analyticsRouteName,
    contentId,
    contentType,
    eligible,
    isWithSeason,
    name,
    removeFromCw,
    seasonId,
    tvShowId,
  ])

  const menuItem: ContentActionMenuItem | null = eligible
    ? {
        id: 'remove_from_continue_watching',
        label: t('remove_from_continue_watching'),
        icon: IconNames.close,
        onSelect: handleRemoveSelect,
      }
    : null

  return {
    eligible,
    handleRemoveSelect,
    menuItem,
  }
}
